import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, Loader2 } from "lucide-react";
import { useLanguage } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';

const MemberRegister = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", display_name: "", phone: "", company: "" });
  const { t } = useLanguage();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.trim()) { toast({ title: "Error", description: t('member.errorRequired'), variant: "destructive" }); return; }
    setLoading(true);
    const { error } = await supabase.from("membership_applications").insert({
      email: form.email.trim(), display_name: form.display_name.trim() || null,
      phone: form.phone.trim() || null, company: form.company.trim() || null,
      user_id: user?.id || null,
    } as any);
    if (error) {
      toast({ title: t('member.submissionFailed'), description: error.message || t('text.somethingWentWrong'), variant: "destructive" });
    } else {
      toast({ title: t('member.success'), description: t('member.successDesc') });
      setForm({ email: "", display_name: "", phone: "", company: "" });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"><UserPlus className="w-6 h-6 text-primary" /></div>
          <CardTitle className="text-2xl">{t('member.signUp')}</CardTitle>
          <CardDescription>{t('member.fillDetails')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('member.email')}</Label>
              <Input id="email" name="email" type="email" required placeholder="you@example.com" value={form.email} onChange={handleChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="display_name">{t('member.displayName')}</Label>
              <Input id="display_name" name="display_name" placeholder="Your name" value={form.display_name} onChange={handleChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{t('member.phone')}</Label>
              <Input id="phone" name="phone" type="tel" placeholder="+1 234 567 890" value={form.phone} onChange={handleChange} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">{t('member.company')}</Label>
              <Input id="company" name="company" placeholder="Company name" value={form.company} onChange={handleChange} />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('member.submitting')}</> : t('member.register')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default MemberRegister;
